import {
    Box,
    Paper,
    TextField,
    Typography,
    Button,
    Stack,
    Chip,
    Alert
} from "@mui/material";
import { Email, Timer, Refresh } from "@mui/icons-material";
import axios from "axios";
import { useState, useRef, useEffect, useContext } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useSnackbar } from "../../components/snackbar";
import { AuthContext } from "../../auth/AuthProvider";

const API_URL = process.env.REACT_APP_API_URL;


function Verify() {
    const { action } = useParams();
    const { setAuthStatus } = useContext(AuthContext); 
    const [otp, setOtp] = useState<string[]>(Array(6).fill(""));
    const { showMessage } = useSnackbar();
    const [resendTimer, setResendTimer] = useState<number>(60);
    const [canResend, setCanResend] = useState<boolean>(false);
    const inputsRef = useRef<(HTMLInputElement | null)[]>([]);
    const navigate = useNavigate();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const instructor_email = queryParams.get("instructorEmail");

    const [loading, setLoading] = useState(false);

    // Resend OTP timer countdown
    useEffect(() => {
        let timer: NodeJS.Timeout;

        if (!canResend && resendTimer > 0) {
            timer = setInterval(() => {
                setResendTimer(prev => {
                    if (prev === 1) setCanResend(true);
                    return prev - 1;
                });
            }, 1000);
        }

        return () => clearInterval(timer);
    }, [resendTimer, canResend]);

    const handleChange = (index: number, value: string) => {
        if (!/^\d?$/.test(value)) return;

        const newOtp = [...otp];
        newOtp[index] = value;
        setOtp(newOtp);

        if (value && index < 5) {
            inputsRef.current[index + 1]?.focus();
        }
    };

    const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
        if (e.key === "Backspace" && !otp[index] && index > 0) {
            inputsRef.current[index - 1]?.focus();
        }
    };

    const handlePaste = (e: React.ClipboardEvent) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData("Text").replace(/\D/g, "").slice(0, 6);
        if (pasted.length === 6) {
            setOtp(pasted.split(""));
            inputsRef.current[5]?.focus();
        }
    };

    const handleSubmit = async () => {
        const code = otp.join("");
        if (code.length !== 6) {
            showMessage("Please enter the complete 6-digit OTP.", 2);
            return;
        }

        try {
            setLoading(true);
            await axios.post(
                `${API_URL}/auth/instructors/otp/verify`,
                {
                    "email": instructor_email,
                    "action": action,
                    "otp": code
                },
                { withCredentials: true }
            );
            setAuthStatus("authenticated");
            navigate("/");
        } catch (err: any) {
            showMessage(err.response?.data?.detail || "Invalid or expired OTP. Please try again.", 0);
        } finally {
            setLoading(false);
        }
    };

    const handleResendOtp = async () => {
        try {
            setLoading(true);
            await axios.post(`${API_URL}/auth/instructors/otp/resend?instructor_email=${instructor_email}`);
            setResendTimer(60);
            setCanResend(false);
            showMessage('OTP Resent', 3);
        } catch (err: any) {
            showMessage("Failed to resend OTP. Please try again.", 0);
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                backgroundColor: "grey.100",
                px: 2,
                display: 'flex',
                flexDirection: 'column', 
                justifyContent: 'center', 
                alignItems: 'center'
            }}
        >   
            <Box maxWidth={500} sx={{ width: { md: "100%" }, border: {md: "1px solid rgb(197, 193, 193)"}, padding: {md: 1}, borderRadius: {md: 2}}}>
            
                <Box textAlign="center" mb={3}>
                    <Email sx={{ fontSize: 30, color: "#3b82f6", mb: 1 }} />
                    <Typography variant="h6" fontWeight={600}>
                        Verify Your Account
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Enter the 6-digit OTP sent to your email.
                    </Typography>
                </Box>

                <Stack direction="row" spacing={1} justifyContent="center" onPaste={handlePaste} mb={2}>
                    {otp.map((digit, index) => (
                        <TextField
                            key={index}
                            inputRef={(el) => (inputsRef.current[index] = el)}
                            value={digit}
                            onChange={(e) => handleChange(index, e.target.value)}
                            onKeyDown={(e) => handleKeyDown(index, e)}
                            inputProps={{
                                maxLength: 1,
                                style: {
                                    textAlign: "center",
                                    fontSize: "1.5rem",
                                    fontWeight: "bold"
                                }
                            }}
                            sx={{
                                width: 50,
                                '& .MuiOutlinedInput-root': {
                                    borderRadius: 2,
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                        borderColor: "#3b82f6",
                                        borderWidth: 2
                                    }
                                }
                            }}
                        />
                    ))}
                </Stack>


                <Button
                    variant="contained"
                    fullWidth
                    onClick={handleSubmit}
                    sx={{
                        mt: 2,
                        py: 1.5,
                        fontWeight: 600,
                        backgroundColor: "#3b82f6",
                        '&:hover': { backgroundColor: "#2563eb" }
                    }}
                    disabled={otp.some(d => !d) || loading}
                >
                    Verify
                </Button>

                <Box mt={3} textAlign="center">
                    {!canResend ? (
                        <Chip
                            icon={<Timer />}
                            label={`Resend OTP in ${resendTimer}s`}
                            variant="outlined"
                            size="small"
                        />
                    ) : (
                        <Button
                            startIcon={<Refresh />}
                            onClick={handleResendOtp}
                            variant="text"
                            size="small"
                            sx={{ textTransform: "none", color: "#3b82f6" }}
                        >
                            Resend OTP
                        </Button>
                    )}
                </Box>
            </Box>
        </Box>
    );
}

export default Verify;
